/**
 * 운영자 화면의 「실제 브랜드 | 테스트」 탭 (261023-test-brand). 브랜드 단위로 나오는 화면마다 단다.
 *
 * - 탭은 **주소의 `?kind=test`** 가 정한다 — 새로고침·링크 공유에도 유지된다. 실제 탭이면 주소에 붙이지 않는다
 * - 화면은 `useBrandKind()` 의 값을 목록·합산 요청의 `brandKind` 로 넘긴다. 서버는 **생략을 실제로** 읽으므로
 *   빠뜨린 요청이 테스트를 섞지 않는다
 */
import { registry } from '@geo-admin/shared/contract/api/index.ts';
import type { Brand } from '@geo-admin/shared/contract/api/registry.ts';
import type { BrandKind } from '@geo-admin/shared/contract/common.ts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useApi } from '../lib/api.ts';
import { cn } from '../lib/utils.ts';

const TABS: readonly { id: BrandKind; label: string }[] = [
  { id: 'real', label: '실제 브랜드' },
  { id: 'test', label: '테스트' },
];

export function useBrandKind(): BrandKind {
  const { kind } = useSearch({ strict: false }) as { kind?: string };
  return kind === 'test' ? 'test' : 'real';
}

export function BrandKindTabs({ className }: { className?: string }) {
  const kind = useBrandKind();
  const navigate = useNavigate();
  const pick = (k: BrandKind) => {
    if (k === kind) return;
    // 다른 검색 값(페이지 등)은 버린다 — 탭이 바뀌면 목록이 달라진다. 엔진 탭(EngineTabs)만 유지한다
    void navigate({
      to: '.',
      search: ((prev: { engine?: string }) => ({
        ...(prev.engine ? { engine: prev.engine } : {}),
        ...(k === 'test' ? { kind: 'test' } : {}),
      })) as never,
    });
  };
  return <Tabs tabs={TABS} value={kind} onChange={pick} className={className} />;
}

/** 같은 모양의 탭 — 화면 안의 다른 갈래(예: 이용 중 | 삭제된 브랜드)도 이 모양으로 맞춘다 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  className,
}: {
  tabs: readonly { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
}) {
  return (
    <div role="tablist" className={cn('flex gap-1', className)}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm',
            value === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** 테스트 브랜드 표시 — 탭 밖(브랜드 상세·선택지)에서 실제와 헷갈리지 않게 */
export function TestBadge() {
  return <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">테스트</span>;
}

/**
 * 탭이 없는 작성 폼(기사·계약서·계정 소속)의 고객 선택지 — 실제 브랜드를 위에, 테스트는 아래 「테스트」 묶음으로.
 * 둘을 한 목록에 그냥 섞지 않는다(이름만으로는 어느 쪽인지 모른다)
 */
export function useBrandChoices(enabled = true): { real: Brand[]; test: Brand[]; all: Brand[] } {
  const real = useApi(registry.listBrands, { query: { brandKind: 'real' } }, { enabled });
  const test = useApi(registry.listBrands, { query: { brandKind: 'test' } }, { enabled });
  const r = real.data?.brands ?? [];
  const t = test.data?.brands ?? [];
  return { real: r, test: t, all: [...r, ...t] };
}

export function BrandOptions({ real, test }: { real: readonly Brand[]; test: readonly Brand[] }) {
  return (
    <>
      {real.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
      {test.length > 0 ? (
        <optgroup label="테스트">
          {test.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </optgroup>
      ) : null}
    </>
  );
}
